import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import * as Sentry from '@sentry/browser'
import axios from 'axios'
import lscache from 'lscache'
import countryToCurrency from 'country-to-currency'
import uuid from 'uuid-random'

import api from '@/api'
import config from '../config'
import { ENV_DEV_OR_STAGING, ENV_HACKERONE } from '@/helpers/environment'
import { SUPPORTED_CURRENCIES, GDPR_COUNTRIES, LS_EXPIRATION_MINUTES } from '@/helpers/constants'
import request, { fstrRequest, marketingRequest } from '@/helpers/request'
import coreFeatures from '@/store/coreFeatures'
import { bmiCalculator } from '@/helpers/userData'
import { filterInvalidKeys } from '@/helpers/utils'

import i18n from './i18n.js'
import survey from './survey.js'
import download from './download.js'
import location from './location.js'
import status from './status.js'
import userFlags from './userFlags.js'
import onetrust from './onetrust.js'
import { palta, convertPaltaSubscription } from '@/modules/Palta'

Vue.use(Vuex)

export default new Vuex.Store({
	modules: {
		onetrust,
		survey,
		i18n,
		download,
		location,
		status,
		coreFeatures,
		userFlags,
		palta
	},

	plugins: [
		createPersistedState({
			storage: {
				getItem: (key) => lscache.get(key),
				setItem: (key, value) => lscache.set(key, value, LS_EXPIRATION_MINUTES),
				removeItem: (key) => lscache.remove(key)
			},
			paths: [
				'auth',
				'absmartly',
				'marketingDeduplicationEventId',
				'location',
				'paid_plan',
				'survey.values',
				'unpaidFlow',
				'userFlags.local',
				'email'
			]
		}),
		createPersistedState({
			storage: window.sessionStorage,
			paths: [
				'geo',
				'name',
				'zip',
				'dateEnd',
				'subscription',
				'additionalSubscription',
				'countdown',
				'lastEmailSendAt',
				'savedPlan',
				'selectedPlan',
				'i18n.query',
				'promocode',
				'main_user_id',
				'download',
				'trialPriceSelected',
				'trialPrice',
				'subscription_price',
				'currency',
				'landingAutoLoading',
				'tax',
				'palta.subscriptions',
				'organicTrafficOptions'
			]
		})
	],

	state: {
		auth: {
			token: null,
			refreshToken: null,
			expireAt: null
		},
		absmartly: {
			exposures: {}
		},
		promocode: null,
		countdown: null,
		countdownIsVisible: false,
		notification: null,
		plans: [],
		subscription: null,
		additionalSubscription: null,
		email: '',
		name: '',
		zip: '',
		lastEmailSendAt: null,
		savedPlan: null,
		selectedPlan: null,

		user: null,
		main_user_id: null,

		trialPrice: 100,
		subscription_price: null,
		paid_plan: null,
		currency: 'usd',
		dateEnd: null,
		trialPriceSelected: false,
		upsaled: false,
		hasWorkbookPurchaseError: false,
		geo: null,
		tax: null,
		landingAutoLoading: false,
		marketingDeduplicationEventId: uuid(),
		// DEV-5375 ML Churn predict experiment
		unpaidFlow: false,
		appTheme: null,
		organicTrafficOptions: {
			paymentConsent: false,
			paymentConsentError: false,
			upsaleConsent: false,
			upsaleConsentError: false
		}
	},

	getters: {
		getAuthCredentials(state) {
			return state.auth
		},

		isAnonymous(state) {
			return state.user && state.user.isAnonymous
		},

		isAuthenticated(state) {
			return state.user && state.user.isThirdParty
		},

		isOnboarded(state) {
			return state.user && state.user.isOnboarded
		},

		/**
		 * Needs to be deleted
		 * @deprecated
		 * @return {string}
		 */
		getUnitSystem(state, getters) {
			let value = getters['survey/getPropertyValue']('measurement_system', true)

			if (!value) {
				value = getters.getCountryCode === 'us' ? 'imperial' : 'metric'
			}
			return value == 'metric' ? 'kg' : 'lbs'
		},

		getHeightUnitSystem(state, getters) {
			let value = getters['survey/getPropertyValue']('measurement_system_height', true)
			// Set defaults
			if (!value) {
				if (getters.getCountryCode === 'us') {
					value = 'ft'
				} else {
					value = 'cm'
				}
			}
			return value
		},

		getWeightUnitSystem(state, getters) {
			let value = getters['survey/getPropertyValue']('measurement_system_weight', true)
			// Set defaults
			if (!value) {
				if (getters.getCountryCode === 'us') {
					value = 'lbs'
				} else if (getters.getCountryCode === 'gb') {
					value = 'st'
				} else {
					value = 'kg'
				}
			}
			return value
		},

		getBmi(state, getters) {
			const weight = getters['survey/getPropertyValue']('weight', false)
			const height = getters['survey/getPropertyValue']('height', false)
			if (!weight || !height) {
				return null
			}
			return bmiCalculator(weight, height)
		},

		getMeasurementSystem: () => (type) => {
			return ['lbs', 'ft'].includes(type) ? 'imperial' : 'metric'
		},

		subscriptionIsActive(state) {
			return state.subscription && !state.subscription.cancelled
		},

		additionalSubscriptionIsActive(state) {
			return state.additionalSubscription && !state.additionalSubscription.cancelled
		},

		getSubscriptionPrice(state) {
			return state.subscription_price || 79.2
		},

		getUserId(state) {
			return state.main_user_id || state.user?.id
		},

		getCountryCode(state) {
			return state.geo?.countryCode
		},

		getGeo(state) {
			return state.geo ?? {}
		},

		getGdprStatus(state) {
			return state.geo?.gdpr
		},

		getIsEUCountry(state) {
			return state.geo?.isEUCountry
		},

		getCurrency(state) {
			return state.currency
		},

		getCurrencyUppercase(state) {
			return state.currency.toUpperCase()
		},

		getAbSmartlyExposures(state) {
			return state.absmartly.exposures
		},
		getAppTheme(state) {
			return state.appTheme ?? 'default'
		},
		getIsCheapAndShortSubscription(state) {
			const isCheapAndShortStripe = [
				config('stripe_cheap_and_short'),
				config('stripe_cheap_and_short_zing'),
				config('stripe_cheap_and_short_upgrade_1_year'),
				config('stripe_cheap_and_short_upgrade_3_month'),
				config('stripe_cheap_and_short_upgrade_6_month'),
				config('stripe_cheap_and_short_upgrade_9_month'),
				config('stripe_cheap_and_short_zing_upgrade')
			].includes(state.subscription?.product_id)

			const isCheapAndShortBraintree = state.subscription?.product_id?.startsWith('pro_short_cheap')

			return [isCheapAndShortStripe, isCheapAndShortBraintree].some(Boolean)
		},
		getOrganicTrafficOptions(state) {
			return state.organicTrafficOptions
		}
	},

	mutations: {
		setUser(state, user) {
			state.user = user

			this._vm.$analytic.setUserId(user.id)

			window.dataLayer?.push({
				user_id: user.id
			})

			Sentry.setUser({
				id: user.id,
				email: user.email
			})
		},

		setAuthCredentials(state, { token, refreshToken, expireAt }) {
			state.auth.token = token
			state.auth.refreshToken = refreshToken
			state.auth.expireAt = expireAt
		},

		addAbSmartlyExposure(state, exposure) {
			const { name, iteration, variantLetter, variantName } = exposure
			const key = `${name}_${iteration}`
			const exposureData = { name, iteration, variantLetter, variantName }

			state.absmartly.exposures[key] = exposureData
		},

		removeAbSmartlyExposure(state, { name, iteration }) {
			Vue.delete(state.absmartly.exposures, `${name}_${iteration}`)
		},

		markOnboarded(state) {
			state.user.isOnboarded = true
		},

		setSubscription(state, subscription) {
			state.subscription = subscription
		},

		setAdditionalSubscription(state, subscription) {
			state.additionalSubscription = subscription
		},

		resetNotification(state) {
			state.notification = null
		},

		setNotification(state, message) {
			state.notification = message
		},

		saveEmail(state, value) {
			state.email = value
			window.email = value
		},

		saveName(state, value) {
			state.name = value
		},

		saveZip(state, value) {
			state.zip = value
		},

		updateCountdown(state, value) {
			state.countdown = value
		},

		toggleCountdown(state, value) {
			state.countdownIsVisible = value
		},

		updateLastEmailSendAt(state, value) {
			state.lastEmailSendAt = value
		},

		subscriptionMarkCanceled(state) {
			state.subscription.cancelled = true
		},

		additionalSubscriptionMarkCanceled(state) {
			state.additionalSubscription.cancelled = true
		},

		setTrialPrice(state, price) {
			state.trialPrice = price
		},

		setSelectedPlan(state, plan) {
			state.selectedPlan = plan
		},

		setPaidPlan(state, plan) {
			state.paid_plan = plan
		},

		setPromocode(state, promocode) {
			state.promocode = promocode
		},

		setSavedPlan(state, plan) {
			state.savedPlan = plan
		},

		setCurrency(state, currency) {
			state.currency = currency
		},

		saveDateEnd(state, date) {
			state.dateEnd = date
		},

		setUpsaled(state, upsaled) {
			state.upsaled = upsaled
		},

		setMainUserId(state, user_id) {
			state.main_user_id = user_id
		},

		markTrialPriceSelected(state, selected) {
			state.trialPriceSelected = selected
		},

		saveSubscriptionPrice(state, price) {
			state.subscription_price = price
		},

		setWorkbookPurchaseError(state, value) {
			state.hasWorkbookPurchaseError = value
		},

		setGeo(state, geo) {
			state.geo = geo
		},

		setTax(state, tax) {
			state.tax = tax
		},

		setLandingAutoLoading(state, value) {
			state.landingAutoLoading = value
		},

		setUnpaidFlow(state, value) {
			state.unpaidFlow = value
		},

		setAppTheme(state, theme) {
			state.appTheme = theme
		},

		setOrganicTrafficOption(state, { key, value } = {}) {
			state.organicTrafficOptions[key] = value
		}
	},

	actions: {
		async fetchUserFromAws(store) {
			try {
				if (store.getters['status/resourceIsPending']('user')) {
					return
				}

				store.commit('status/setResourcePending', 'user')

				const response = await api.getUser()
				store.commit('setUser', response.data)

				await store.dispatch('loadSubscription')

				if (response.data.data) store.dispatch('mergeUserData')
			} catch (e) {
				this._vm.$analytic.logEvent('Get user error', { e })
				Sentry.captureException(e)
			} finally {
				store.commit('status/unsetResourcePending', 'user')
			}
		},

		async loadSubscription(store) {
			try {
				let url = config('SimpleAPIUrl') + 'v1/api/subscription?user_id=' + store.state.user.id
				let response = await fetch(url)
				let data = await response.json()

				let subscription = Object.keys(data.data).length > 0 ? data.data : null

				if (subscription || subscription?.user_id !== store.state.user.id) {
					store.commit('setSubscription', subscription)
				}

				await store.dispatch('loadPaltaSubscription')
			} catch (e) {
				store.commit('setSubscription', null)
				this._vm.$analytic.logEvent('Get subscription error', {
					e
				})
				// Sentry.captureException(e)
			}
		},

		async loadAdditionalSubscription(store) {
			try {
				const url = `${config('SimpleAPIUrl')}v1/api/users/${store.state.user.id}/additional-subscription`
				const response = await fetch(url)
				const data = await response.json()

				const subscription = Object.keys(data.data).length > 0 ? data.data : null

				if (subscription || subscription?.user_id !== store.state.user.id) {
					store.commit('setAdditionalSubscription', subscription)
				}
			} catch (e) {
				Sentry.captureException(e)
			}
		},

		loadPaltaSubscription(store, params = {}) {
			return store
				.dispatch('palta/fetchSubscriptions', {
					user_id: store.getters['getUserId'],
					...params
				})
				.then(({ subscription, status }) => {
					if (subscription) {
						store.commit('setSubscription', convertPaltaSubscription(subscription, store.state.user))
					}
					return {
						status,
						subscription: store.state.subscription
					}
				})
		},

		makePaltaPurchase(store, params) {
			return store.dispatch('palta/purchase', params).then((result) => {
				return store
					.dispatch('loadPaltaSubscription', {
						ident: params.ident,
						user_id: store.getters['getUserId']
					})
					.then(() => {
						return result
					})
			})
		},

		async cancelSubscription(store, { force = false, additional = false }) {
			if (store.state.subscription.store == 'stripe') {
				let code = await api.generateLoginCode()

				return await fetch(config('SimpleAPIUrl') + 'v1/api/stripe/subscription', {
					method: 'DELETE',
					body: JSON.stringify({
						user_id: store.state.user.id,
						force,
						is_additional: additional,
						code: code.data
					})
				}).then((response) => response.json())
			} else if (store.state.subscription.store == 'braintree') {
				let code = await api.generateLoginCode()

				return fetch(config('SimpleAPIUrl') + 'v1/api/braintree/subscription', {
					method: 'DELETE',
					body: JSON.stringify({
						is_additional: additional,
						code: code.data
					})
				})
			} else if (store.state.subscription.store == 'palta') {
				return await this.dispatch('palta/cancelSubscription', {
					user_id: store.state.user.id,
					subscription_id: store.state.subscription.id
				})
			} else {
				let code = await api.generateLoginCode()
				let url = config('SimpleAPIUrl') + 'v1/api/paypal/subscription?code=' + code.data
				await fetch(url, {
					method: 'DELETE',
					body: JSON.stringify({
						is_additional: additional
					})
				})
			}
		},

		checkExistingUserByEmail(store, params) {
			return api.userCheck(params).then((response) => {
				let user_id = response.data

				store.commit('setMainUserId', user_id)

				return user_id
			})
		},

		async sendConfirmEmail(store, params) {
			await api.sendConfirmEmail({
				email: store.state.email,
				...params
			})
			store.commit('updateLastEmailSendAt', new Date())
		},

		logUserRegistration(store) {
			let email = store.state.email
			let name = store.state.user.name == 'Anonymous' ? '' : store.state.user.name
			let user_id = store.state.user.id

			fetch(`${config('SimpleAPIUrl')}v1/api/marketing/user`, {
				method: 'POST',
				body: JSON.stringify({
					user_id,
					email,
					name
				})
			}).catch((error) => {
				console.error(error.response)
			})
		},

		/**
		 * Method for sending marketing campaign data to Payment API
		 * @param store
		 * @param {object} data
		 * @param {'facebook'|'google'|'impact'|'twitter'} data.source - source of campaign
		 * @param {object} data.params
		 */
		sendMarketingCampaignData(store, data) {
			const { source, params } = data

			return fetch(`${config('SimpleAPIUrl')}v1/api/campaign-tracker/${source}`, {
				method: 'POST',
				body: JSON.stringify(params)
			})
				.then(() => {
					if (ENV_DEV_OR_STAGING) {
						/* eslint-disable no-console */
						console.groupCollapsed(
							`%cMarketing campaign tracker [debug]: %c${source}`,
							'font-weight: 400',
							'color: #19ffb2'
						)
						console.log(data)
						console.groupEnd()
						/* eslint-enable no-console */
					}
				})
				.catch((error) => {
					Sentry.withScope((scope) => {
						scope.setExtras({ source, ...params })
						scope.setTags({
							marketingCampaignTrackerError: true
						})
						Sentry.captureException(error)
					})
				})
		},

		async createMeasurement(store) {
			return await api.createMeasurement({
				records: [
					{
						type: 'Weight',
						value: store.state.user.weight,
						source: 'Manual',
						date: new Date(),
						secondsFromGMT: -new Date().getTimezoneOffset() * 60
					}
				]
			})
		},

		/**
		 * Get country code and currency from https://assets.simple.org/geo.json
		 * @returns {Promise<{countryCode: String, currency: String}>}
		 */
		async fetchSimpleGeo(store) {
			return request(config('GeoAPIUrl'))
				.then((response) => {
					let { city, continent, country, isEUCountry, region, regionCode, postalCode } = response

					if (ENV_DEV_OR_STAGING || ENV_HACKERONE) {
						const queryParamCountry = store.getters['location/getQueryParam']('country')

						if (/^[A-Z]{2}$/.test(queryParamCountry)) {
							country = queryParamCountry
						}
					}

					const result = {
						countryCode: country.toLowerCase(),
						currency: countryToCurrency[country],
						continentCode: continent,
						city: city?.toLowerCase(),
						isEUCountry: isEUCountry === '1',
						regionName: region?.toLowerCase(),
						region: regionCode?.toLowerCase(),
						zip: postalCode
					}

					return filterInvalidKeys(result)
				})
				.catch((error) => {
					Sentry.captureException(error)
				})
		},

		/**
		 * Fetch geo data and set it to the store
		 * @param store
		 * @returns {Promise<void>}
		 */
		async fetchGeo(store, { country }) {
			if (store.state.geo && !country) {
				return
			}
			const simpleGeoResult = await store.dispatch('fetchSimpleGeo')

			if (!simpleGeoResult) {
				return
			}

			let currencyCode = 'usd'
			let gdpr = false

			if (SUPPORTED_CURRENCIES.includes(simpleGeoResult.currency)) {
				currencyCode = simpleGeoResult.currency?.toLowerCase()
			}

			if (GDPR_COUNTRIES.includes(simpleGeoResult.countryCode?.toUpperCase())) {
				gdpr = true
			}

			store.commit('setCurrency', currencyCode)
			store.commit('setGeo', {
				...simpleGeoResult,
				gdpr
			})
		},

		async fetchUserLocalTax(store) {
			return await fetch(`${config('SimpleAPIUrl')}v1/api/taxrate/match`, {
				method: 'POST',
				body: JSON.stringify({
					user_id: store.state.user.id,
					geo_data: store.state.geo
				})
			})
				.then((response) => response.json())
				.then((data) => {
					store.commit('setTax', data.web_rate)

					return data.web_rate
				})
		},

		logout() {
			localStorage.clear()
			window.location.href = '/'
		},

		mergeUserData({ state }) {
			let user = state.user
			let data = user.data ? JSON.parse(user.data) : {}

			Object.keys(state.user)
				.filter((key) => key in state.survey.values)
				.forEach((key) => {
					if (key == 'data') {
						Object.keys(data)
							.filter((data_key) => data_key in state.survey.values.data)
							.forEach((data_key) => {
								state.survey.values.data[data_key] = data[data_key] === 'null' ? null : data[data_key]
							})
					} else {
						const surveyValues = state.survey.values[key]
						const userValues = state.user[key]

						if (Array.isArray(surveyValues) && Array.isArray(userValues)) {
							const uniqueValues = Array.from(new Set([...surveyValues, ...userValues]))
							state.survey.values[key] = uniqueValues
						} else {
							state.survey.values[key] = userValues
						}
					}
				})
		},

		async setPromocodeByName({ commit }, promocode) {
			try {
				const { data } = await axios.get(`${config('SimpleAPIUrl')}v1/api/marketing/promocode`, {
					params: {
						promocode
					}
				})

				// TODO: We need to map new data to old models in order to support existing implementations
				const oldPromocodeModel = {
					id: data.code,
					label: data.label,
					trial: data.trial,
					trial_price: data.trial_price,
					trial_percent: data.trial_percent,
					discount: Boolean(data.percent),
					discount_value: data.percent
				}
				commit('setPromocode', oldPromocodeModel || null)
			} catch (e) {
				commit('setPromocode', null)
				throw e
			}
		},

		async activateFreebieAccess({ getters }, freebieCode) {
			return await request(`${config('SimpleAPIUrl')}v1/api/free-access/activate`, {
				method: 'POST',
				data: {
					code: freebieCode,
					user_id: getters.getUserId
				}
			})
		},

		sendAbSmartlyExposuresToBraze(store, exposures) {
			if (!exposures) {
				exposures = store.state.absmartly.exposures
			}

			for (let exposureName in exposures) {
				const { name, iteration, variantLetter, variantName } = exposures[exposureName]
				const underscoredName = name.replaceAll(' ', '_')

				this._vm.$appboy.logCustomEvent('Experiment - Exposure', {
					Name: name,
					Iteration: `i${iteration}`,
					Variant: variantLetter,
					VariantName: variantName
				})
				this._vm.$appboy.addToCustomAttributeArray(
					'active_test_groups_web',
					`${underscoredName}(${variantLetter})(${`i${iteration}`})`
				)
			}
		},

		async saveZipCode({ getters }, options) {
			return await request(`${config('SimpleAPIUrl')}v1/customers/${getters.getUserId}`, {
				method: 'PATCH',
				data: { ...options }
			})
		},

		async saveUserMarketingParams(context, params) {
			try {
				await marketingRequest('/actions/user_click', {
					method: 'POST',
					data: params
				})
			} catch (error) {
				// Just let the user pass anyway
			}
		},

		async getLtvValueByUserParams(context, params) {
			return await marketingRequest('/actions/ltv', {
				method: 'GET',
				params: params
			})
		},

		async saveReferralCode({ getters }) {
			try {
				await fstrRequest(`${config('FstrUsersAPIUrl')}v1/user/referral/${getters.getUserId}/activate`, {
					method: 'POST',
					data: {
						ref_code: getters['location/getReferralCode']
					}
				})
			} catch (error) {
				Sentry.withScope((scope) => {
					scope.setExtras({ ref_code: getters['location/getReferralCode'], user_id: getters.getUserId })
					scope.setTags({
						referralCode: true
					})
					Sentry.captureException(error)
				})
			}
		}
	}
})
